import { useEffect, useRef, useState } from "react";
import { useCredentialRevealGuard } from "../../auth/CredentialRevealGuard";
import { Button } from "../Button";
import { scheduleClipboardClear } from "./credentialClipboard";

interface CredentialRevealPanelProps {
  username?: string;
  password: string;
}

export function CredentialRevealPanel({ username, password }: CredentialRevealPanelProps) {
  const { beginReveal, canLeave, clearReveal, setConfirmed } = useCredentialRevealGuard();
  const [revealed, setRevealed] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const timeouts = useRef<number[]>([]);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    beginReveal();
    return () => {
      mounted.current = false;
      clearReveal();
    };
  }, [beginReveal, clearReveal]);

  function revealPassword() {
    setRevealed(true);
    beginReveal();
    setAnnouncement("Senha temporária mostrada.");
  }

  async function copy(value: string, label: string) {
    try {
      await navigator.clipboard.writeText(value);
      setAnnouncement(`${label} copiado.`);
      timeouts.current.push(scheduleClipboardClear(value, () => {
        if (mounted.current) setAnnouncement("Não foi possível limpar automaticamente o clipboard. Apague o conteúdo manualmente.");
      }));
    } catch {
      setAnnouncement(`Não foi possível copiar ${label.toLowerCase()}.`);
    }
  }

  return (
    <section className="credential-reveal" aria-label="Credenciais temporárias">
      {username && (
        <div className="credential-reveal__item">
          <span>Usuário</span>
          <strong>{username}</strong>
          <Button type="button" variant="secondary" onClick={() => void copy(username, "Usuário")}>Copiar usuário</Button>
        </div>
      )}
      <div className="credential-reveal__item">
        <span>Senha temporária</span>
        {revealed ? <strong className="credential-reveal__password">{password}</strong> : <strong className="credential-reveal__masked" aria-label="Senha temporária mascarada">••••••••••••</strong>}
        {revealed ? (
          <Button type="button" variant="secondary" onClick={() => void copy(password, "Senha")}>Copiar senha</Button>
        ) : (
          <Button type="button" onClick={revealPassword}>Mostrar senha temporária</Button>
        )}
      </div>
      {revealed && (
        <label className="credential-confirmation">
          <input type="checkbox" checked={canLeave} onChange={(event) => setConfirmed(event.target.checked)} />
          <span>Copiei a senha e vou entregá-la com segurança</span>
        </label>
      )}
      {revealed && <p className="credential-reveal__notice">O clipboard será conferido e limpo após 60 segundos quando ainda contiver este valor. A limpeza não alcança histórico ou outros aplicativos.</p>}
      <p className="credential-reveal__announcement" role="status" aria-live="polite">{announcement}</p>
    </section>
  );
}
